import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import "../Pages/Download.css";
import { sweetConfirm } from "../utils/sweetAlerts";
import { RUNTIME_PUBLIC_BASE } from "../utils/runtimeBase";

const API = RUNTIME_PUBLIC_BASE || "";

const defaultSectionForm = {
  id: null,
  title: "",
  description: "",
  thumbnail: null,
};

const defaultItemForm = {
  title: "",
  description: "",
  thumbnail: null,
  file: null,
};

function DownloadManager() {
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [sectionForm, setSectionForm] = useState(defaultSectionForm);
  const [itemForm, setItemForm] = useState(defaultItemForm);
  const [sectionPreview, setSectionPreview] = useState("");
  const [itemPreview, setItemPreview] = useState("");
  const [itemEdits, setItemEdits] = useState({});
  const [savingSection, setSavingSection] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const activeSection = useMemo(
    () => sections.find((item) => item.id === activeSectionId) || null,
    [activeSectionId, sections]
  );

  const loadSections = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/download-sections`);
      setSections(Array.isArray(response.data) ? response.data : []);
    } catch {
      setError("Failed to load download settings");
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  useEffect(() => {
    if (!sectionForm.thumbnail) return undefined;
    const nextUrl = URL.createObjectURL(sectionForm.thumbnail);
    setSectionPreview(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [sectionForm.thumbnail]);

  useEffect(() => {
    if (!itemForm.thumbnail) return undefined;
    const nextUrl = URL.createObjectURL(itemForm.thumbnail);
    setItemPreview(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [itemForm.thumbnail]);

  const resetSectionForm = () => {
    setSectionForm(defaultSectionForm);
    setSectionPreview("");
  };

  const resetItemForm = () => {
    setItemForm(defaultItemForm);
    setItemPreview("");
  };

  const saveSection = async () => {
    if (!sectionForm.title.trim()) {
      setError("Section title is required");
      return;
    }

    if (!sectionForm.id && !sectionForm.thumbnail) {
      setError("Section thumbnail is required");
      return;
    }

    try {
      setSavingSection(true);
      setError("");
      setMessage("");

      const formData = new FormData();
      formData.append("title", sectionForm.title.trim());
      formData.append("description", sectionForm.description.trim());
      if (sectionForm.thumbnail) {
        formData.append("thumbnail", sectionForm.thumbnail);
      }

      if (sectionForm.id) {
        await axios.post(`${API}/api/admin/download-sections/${sectionForm.id}`, formData);
        setMessage("Download section updated successfully");
      } else {
        await axios.post(`${API}/api/admin/download-sections`, formData);
        setMessage("Download section created successfully");
      }

      resetSectionForm();
      loadSections();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Section save failed");
    } finally {
      setSavingSection(false);
    }
  };

  const editSection = (section) => {
    setSectionForm({
      id: section.id,
      title: section.title || "",
      description: section.description || "",
      thumbnail: null,
    });
    setSectionPreview(section.thumbnail ? `${API}/uploads/${section.thumbnail}` : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteSection = async (sectionId) => {
    if (!(await sweetConfirm("Delete this section and all its files?", { confirmButtonText: "Yes, Delete" }))) return;

    try {
      await axios.delete(`${API}/api/admin/download-sections/${sectionId}`);
      setMessage("Download section deleted successfully");
      if (activeSectionId === sectionId) setActiveSectionId(null);
      if (sectionForm.id === sectionId) resetSectionForm();
      loadSections();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Section delete failed");
    }
  };

  const addItem = async () => {
    if (!activeSection) return;
    if (!itemForm.title.trim() || !itemForm.file) {
      setError("Title and file are required");
      return;
    }

    try {
      setSavingItem(true);
      setError("");
      setMessage("");

      const formData = new FormData();
      formData.append("title", itemForm.title.trim());
      formData.append("description", itemForm.description.trim());
      formData.append("file", itemForm.file);
      if (itemForm.thumbnail) {
        formData.append("thumbnail", itemForm.thumbnail);
      }

      await axios.post(`${API}/api/admin/download-sections/${activeSection.id}/items`, formData);
      setMessage("Download item added successfully");
      resetItemForm();
      loadSections();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Item add failed");
    } finally {
      setSavingItem(false);
    }
  };

  const setItemEdit = (itemId, currentItem, nextValue) => {
    setItemEdits((prev) => ({
      ...prev,
      [itemId]: {
        title: prev[itemId]?.title ?? currentItem.title ?? "",
        description: prev[itemId]?.description ?? currentItem.description ?? "",
        thumbnail: prev[itemId]?.thumbnail ?? null,
        file: prev[itemId]?.file ?? null,
        preview: prev[itemId]?.preview ?? "",
        saving: prev[itemId]?.saving ?? false,
        ...nextValue,
      },
    }));
  };

  const saveItem = async (item) => {
    const edit = itemEdits[item.id] || {};

    try {
      setError("");
      setMessage("");
      setItemEdit(item.id, item, { saving: true });

      const formData = new FormData();
      formData.append("title", edit.title ?? item.title ?? "");
      formData.append("description", edit.description ?? item.description ?? "");
      if (edit.thumbnail) formData.append("thumbnail", edit.thumbnail);
      if (edit.file) formData.append("file", edit.file);

      await axios.post(`${API}/api/admin/download-items/${item.id}`, formData);
      setMessage("Download item updated successfully");
      setItemEdits((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      loadSections();
    } catch (requestError) {
      setItemEdit(item.id, item, { saving: false });
      setError(requestError.response?.data?.message || "Item update failed");
    }
  };

  const deleteItem = async (itemId) => {
    if (!(await sweetConfirm("Delete this file item?", { confirmButtonText: "Yes, Delete" }))) return;

    try {
      await axios.delete(`${API}/api/admin/download-items/${itemId}`);
      setMessage("Download item deleted successfully");
      loadSections();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Item delete failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid" style={{ display: "grid", gap: "22px" }}>
        <div>
          <h3 style={{ marginBottom: "6px" }}>Download Page Setting</h3>
          <p className="text-muted mb-0">Create section-wise download categories like Course Syllabus, Study Material, E-book and manage files under each section.</p>
        </div>

        {message ? <div className="alert alert-success mb-0">{message}</div> : null}
        {error ? <div className="alert alert-danger mb-0">{error}</div> : null}

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">{sectionForm.id ? "Edit Download Section" : "Create Download Section"}</h5>
                <div className="text-muted">This section will be visible as the main download category on the public page.</div>
              </div>
              {sectionForm.id ? (
                <button type="button" className="btn btn-outline-secondary" onClick={resetSectionForm}>
                  Cancel Edit
                </button>
              ) : null}
            </div>

            <div className="row g-3 align-items-start">
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Section Title</label>
                <input className="form-control" value={sectionForm.title} onChange={(event) => setSectionForm((prev) => ({ ...prev, title: event.target.value }))} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Section Thumbnail</label>
                <input type="file" className="form-control" onChange={(event) => setSectionForm((prev) => ({ ...prev, thumbnail: event.target.files?.[0] || null }))} />
              </div>
              <div className="col-lg-4">
                <label className="form-label fw-semibold">Preview</label>
                <div className="border rounded-4 bg-light p-2">
                  {sectionPreview ? (
                    <img src={sectionPreview} alt="Section preview" style={{ width: "100%", height: "116px", objectFit: "cover", borderRadius: "12px" }} />
                  ) : (
                    <div className="text-muted d-flex align-items-center justify-content-center" style={{ height: "116px" }}>No thumbnail selected</div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Section Description</label>
                <textarea className="form-control" rows="4" value={sectionForm.description} onChange={(event) => setSectionForm((prev) => ({ ...prev, description: event.target.value }))} />
              </div>
              <div className="col-12 d-flex justify-content-end">
                <button type="button" className="btn btn-primary px-4" onClick={saveSection} disabled={savingSection}>
                  {savingSection ? "Saving..." : sectionForm.id ? "Update Section" : "Create Section"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {!activeSection ? (
          <>
            <div className="download-section-list">
              {sections.map((section) => (
                <button key={section.id} type="button" className="download-section-card" onClick={() => setActiveSectionId(section.id)}>
                  <div className="download-section-thumb">
                    <img src={`${API}/uploads/${section.thumbnail}`} alt={section.title} />
                  </div>
                  <div className="download-section-copy">
                    <h3>{section.title}</h3>
                  </div>
                </button>
              ))}
            </div>

            {!sections.length ? <div className="download-empty">No download section created yet.</div> : null}
          </>
        ) : (
          <div className="download-panel">
            <div className="download-panel-heading">
              <div>
                <h2>{activeSection.title}</h2>
                <p>{activeSection.description || "No description added for this section yet."}</p>
              </div>
              <div className="download-panel-actions">
                <div className="download-count">{activeSection.items?.length || 0} files</div>
                <button type="button" className="download-back-button" onClick={() => setActiveSectionId(null)}>
                  Back To Sections
                </button>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Section Control</h5>
                    <div className="text-muted">Edit section thumbnail/title/description or add downloadable files under this section.</div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => editSection(activeSection)}>
                      Edit Section
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deleteSection(activeSection.id)}>
                      Delete Section
                    </button>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-lg-3">
                    <label className="form-label fw-semibold">File Title</label>
                    <input className="form-control" value={itemForm.title} onChange={(event) => setItemForm((prev) => ({ ...prev, title: event.target.value }))} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label fw-semibold">Thumbnail</label>
                    <input type="file" className="form-control" onChange={(event) => setItemForm((prev) => ({ ...prev, thumbnail: event.target.files?.[0] || null }))} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label fw-semibold">Upload File</label>
                    <input type="file" className="form-control" onChange={(event) => setItemForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label fw-semibold">Preview</label>
                    <div className="border rounded-4 bg-light p-2">
                      {itemPreview ? (
                        <img src={itemPreview} alt="Item preview" style={{ width: "100%", height: "116px", objectFit: "cover", borderRadius: "12px" }} />
                      ) : (
                        <div className="text-muted d-flex align-items-center justify-content-center" style={{ height: "116px" }}>No thumbnail</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-9">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea className="form-control" rows="3" value={itemForm.description} onChange={(event) => setItemForm((prev) => ({ ...prev, description: event.target.value }))} />
                  </div>
                  <div className="col-lg-3 d-flex align-items-end">
                    <button type="button" className="btn btn-primary w-100" onClick={addItem} disabled={savingItem}>
                      {savingItem ? "Adding..." : "Add File"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="download-grid">
              {(activeSection.items || []).map((item) => {
                const edit = itemEdits[item.id];

                return (
                  <div key={item.id} className="download-item-card">
                    <img src={(edit?.preview || (item.thumbnail ? `${API}/uploads/${item.thumbnail}` : "https://placehold.co/900x600/e2e8f0/475569?text=File"))} alt={item.title} />
                    <div className="download-item-body">
                      <input
                        className="form-control form-control-sm mb-2"
                        value={edit?.title ?? item.title ?? ""}
                        placeholder="Title"
                        onChange={(event) => setItemEdit(item.id, item, { title: event.target.value })}
                      />
                      <textarea
                        className="form-control form-control-sm mb-2"
                        rows="3"
                        value={edit?.description ?? item.description ?? ""}
                        placeholder="Description"
                        onChange={(event) => setItemEdit(item.id, item, { description: event.target.value })}
                      />
                      <span className="download-file-name">{item.file_name || "Attached file"}</span>
                      <input
                        type="file"
                        className="form-control form-control-sm mb-2"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setItemEdit(item.id, item, { file });
                        }}
                      />
                      <input
                        type="file"
                        className="form-control form-control-sm mb-2"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          const preview = file ? URL.createObjectURL(file) : "";
                          setItemEdit(item.id, item, { thumbnail: file, preview });
                        }}
                      />
                      <button type="button" className="btn btn-outline-primary btn-sm w-100 mb-2" onClick={() => saveItem(item)} disabled={edit?.saving}>
                        {edit?.saving ? "Saving..." : "Save File"}
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm w-100" onClick={() => deleteItem(item.id)}>
                        Delete File
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {!activeSection.items?.length ? <div className="download-empty mt-4">No file uploaded in this section yet.</div> : null}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default DownloadManager;



